import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Plus, Edit2, Trash2, ArrowLeft, User, 
  Heart, Calendar, Droplet, Shield, ChevronRight,
  UserPlus, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilyPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    role: 'member',
    notes: ''
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('sehatSetu_token');
      const response = await axios.get(`${API_URL}/family/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching family members:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!formData.name || !formData.relationship) {
      toast.error('Please fill in name and relationship');
      return;
    }

    try {
      const token = localStorage.getItem('sehatSetu_token');
      await axios.post(`${API_URL}/family/members`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${formData.name} added to family!`);
      setShowAddDialog(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error adding family member:', error);
      toast.error('Failed to add family member');
    }
  };

  const handleEditMember = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    try {
      const token = localStorage.getItem('sehatSetu_token');
      await axios.put(`${API_URL}/family/members/${editingMember.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated!');
      setShowEditDialog(false);
      setEditingMember(null);
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error updating family member:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name}? This will also delete all their health data.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('sehatSetu_token');
      await axios.delete(`${API_URL}/family/members/${member.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${member.name} removed from family`);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting family member:', error);
      toast.error('Failed to remove family member');
    }
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      relationship: member.relationship || '',
      date_of_birth: member.date_of_birth || '',
      gender: member.gender || '',
      blood_group: member.blood_group || '',
      role: member.role || 'member',
      notes: member.notes || ''
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      relationship: '',
      date_of_birth: '',
      gender: '',
      blood_group: '',
      role: 'member',
      notes: ''
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      self: 'Account Owner',
      grandparent: 'Grandparent',
      parent: 'Parent',
      spouse: 'Spouse',
      child: 'Child',
      sibling: 'Sibling',
      other: 'Other'
    };
    return labels[relationship] || relationship;
  };

  const getRoleIcon = (role) => {
    if (role === 'admin') return <Crown className="h-3 w-3 text-amber-500" />;
    if (role === 'view_only') return <Shield className="h-3 w-3 text-slate-400" />;
    return null;
  };

  const relationships = [
    { value: 'grandparent', label: 'Grandparent (Dada/Dadi/Nana/Nani)' },
    { value: 'parent', label: 'Parent (Father/Mother)' },
    { value: 'spouse', label: 'Spouse (Husband/Wife)' },
    { value: 'child', label: 'Child (Son/Daughter)' },
    { value: 'sibling', label: 'Sibling (Brother/Sister)' },
    { value: 'other', label: 'Other' }
  ];

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#0D2340] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Family Health Vault</h1>
              <p className="text-white/70 text-sm">Manage health profiles for your loved ones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Family Health Management</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Add family members and manage their health records all in one place. 
                  Perfect for helping parents and grandparents track their health.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members List */}
        <div className="space-y-3">
          {members.map((member) => (
            <Card 
              key={member.id} 
              className={`hover:shadow-md transition-shadow cursor-pointer ${member.is_self ? 'ring-2 ring-[#0D2340]/20' : ''}`}
              onClick={() => {
                // Navigate to dedicated member dashboard
                navigate(`/family/member?id=${member.id}`);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0"
                    style={{ backgroundColor: member.avatar_color || '#0D2340' }}
                  >
                    {member.is_self ? (
                      <User className="h-6 w-6" />
                    ) : (
                      getInitials(member.name)
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {member.is_self ? 'Me' : member.name}
                      </h3>
                      {getRoleIcon(member.role)}
                      {member.is_self && (
                        <span className="text-xs bg-[#0D2340]/10 text-[#0D2340] px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">
                      {getRelationshipLabel(member.relationship)}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {member.blood_group && (
                        <span className="flex items-center gap-1">
                          <Droplet className="h-3 w-3" />
                          {member.blood_group}
                        </span>
                      )}
                      {member.date_of_birth && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(member.date_of_birth).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#0D2340] mt-2 font-medium">
                      Tap to view health records →
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(member);
                      }}
                      className="h-9 w-9 p-0"
                      data-testid={`edit-member-${member.id}`}
                    >
                      <Edit2 className="h-4 w-4 text-slate-500" />
                    </Button>
                    {!member.is_self && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMember(member);
                        }}
                        className="h-9 w-9 p-0 hover:bg-red-50"
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                    <ChevronRight className="h-5 w-5 text-slate-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add Member Button */}
        <Button
          onClick={() => {
            resetForm();
            setShowAddDialog(true);
          }}
          className="w-full h-14 bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] hover:opacity-90"
          data-testid="add-family-member-btn"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add Family Member
        </Button>

        {/* Tips */}
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <h4 className="font-medium text-slate-700 mb-2">Tips for Family Health Management</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• Help elderly parents set up their medical history</li>
              <li>• Track children's vaccinations and growth</li>
              <li>• Keep emergency contacts for each family member</li>
              <li>• Generate separate health reports for doctor visits</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#0D2340]" />
              Add Family Member
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Relationship *</Label>
              <Select
                value={formData.relationship}
                onValueChange={(v) => setFormData({ ...formData, relationship: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {relationships.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Blood Group</Label>
              <Select
                value={formData.blood_group}
                onValueChange={(v) => setFormData({ ...formData, blood_group: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  {bloodGroups.map((bg) => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member (Can view & edit own data)</SelectItem>
                  <SelectItem value="view_only">View Only (For elderly)</SelectItem>
                  <SelectItem value="child">Child (Limited access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddMember}
              className="bg-[#0D2340] hover:bg-[#0D2340]/90"
            >
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#0D2340]" />
              Edit {editingMember?.is_self ? 'My Profile' : editingMember?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            {!editingMember?.is_self && (
              <div>
                <Label>Relationship</Label>
                <Select
                  value={formData.relationship}
                  onValueChange={(v) => setFormData({ ...formData, relationship: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {relationships.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Blood Group</Label>
              <Select
                value={formData.blood_group}
                onValueChange={(v) => setFormData({ ...formData, blood_group: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  {bloodGroups.map((bg) => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!editingMember?.is_self && (
              <div>
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="view_only">View Only</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditMember}
              className="bg-[#0D2340] hover:bg-[#0D2340]/90"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FamilyPage;
